'use client';

/**
 * Employee Portal — rich dashboard landing.
 *
 * Renders the same <PortalPreview> component that the admin sees in the
 * view-as impersonation preview (`app/(portal-preview)/user-portal/[employeeId]/page.tsx`),
 * but powered by portal-JWT-scoped hooks for the LOGGED-IN recipient instead
 * of admin-impersonated employeeId params.
 *
 * Regression that this fixes: NC only built the rich portal mockup inside
 * the admin builder (<PortalPreview>). The actual employee /portal route was
 * an HFv3-original 3-card MVP. This commit makes both routes render the same
 * <PortalPreview> component with real data.
 *
 * Data-fetch strategy (portal JWT via fetchPortalApi — no admin JWT required):
 *   - portalAuthApi.getRecipient()                  → identity (no round-trip)
 *   - usePublicPortalSettings(workspaceId)           → portal settings/theme
 *   - usePortalNewsFeed()                            → published news (portal JWT)
 *   - usePortalGamificationSettings() + usePortalBadgePacks() → badge pack + levels (portal JWT)
 *   - usePortalPointsHistory({ limit: 200 })         → points activity (portal JWT)
 *   - usePortalEarnedBadges()                        → earned badges (portal JWT)
 *   - usePortalLeaderboard({ scope, period, limit }) → leaderboard (portal JWT)
 *   - usePortalEnrollments()                         → training enrollments (portal JWT)
 *
 * The layout's <header> is retained (it provides nav links). PortalPreview is
 * mounted with inline={true} which suppresses its own internal header so the
 * two don't stack.
 */

import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PortalPreview } from '@/app/settings/portal/_components/portal-preview';
import { CelebrationProvider } from '@/app/(portal)/_components/celebrations/celebration-provider';
import { PortalChallengesSection } from '@/app/(portal)/_components/challenges/portal-challenges-section';
import { useCelebrationDetector } from '@/app/(portal)/_components/celebrations/celebration-detector';
import type { PortalUser } from '@/lib/user-portal/types';
import type { BadgeAwardEntry, LevelAwardEntry, PointsAwardEntry } from '@/lib/gamification/types';
import {
  transformNewsToProps,
  transformToTrainingSlides,
  transformToCourseCards,
  transformPointsHistory,
  transformToBadgeCards,
  // FIX #686: transformToCertificateCards removed — certificates now come
  // from usePortalCertificates (actual issued certs) not enrollment status.
  findActiveBadgePack,
  earnedBadgeKeys,
  awardsForHeldBadges,
  celebratableAwards,
  celebratableLevelUps,
  buildLevelProgress,
  achievedBadgeIdsIn,
} from '@/lib/user-portal/transform-portal-data';

// Portal-scoped hooks (portal JWT via fetchPortalApi)
import { usePublicPortalSettings } from '@/components/portal/hooks/use-portal-settings';
import { usePortalNewsFeed } from '@/components/portal/hooks/use-portal-news';
import {
  usePortalGamificationSettings,
  usePortalBadgePacks,
  usePortalPointsHistory,
  usePortalEarnedBadges,
  usePortalLeaderboard,
  usePortalPointsSummary,
  usePortalLevels,
  usePortalLevelLadder,
} from '@/components/portal/hooks/use-portal-gamification';
import { usePortalEnrollments, usePortalDashboard } from '@/components/portal/hooks/use-portal-training';
// FIX #686: import real certificate hook so Certificates tab reads actual
// issued certs (course_completions) rather than enrollment status.
import { usePortalCertificates } from '@/components/portal/hooks/use-portal-certificates';
import { useLatestPolicyItems } from '@/components/knowledge/portal/latest-policies';
import { useKnowledgeAskStore } from '@/components/knowledge/portal/ask-store';
import { KnowledgeAskAiButton } from '@/components/knowledge/portal/ask-ai-button';

// Identity — no round-trip needed; set at direct-login time
import { portalAuthApi } from '@/lib/api/portal-auth';
import {
  buildPortalLoginHref,
  isPortalTokenDeadLocally,
  isResolvableWorkspaceId,
  PORTAL_WS_STORAGE_KEY,
} from '@/lib/user-portal/portal-login-redirect';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/lib/i18n';
import { isModuleDisabledError } from '@/lib/api/module-disabled';
import type { PortalGamificationSettings, PointTransaction } from '@/lib/types/gamification';
import type { GamificationSettings as LegacyGamSettings } from '@/lib/gamification/types';
import type { PortalEnrollment } from '@/components/portal/hooks/use-portal-training';

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

/**
 * Bridge the portal's gamification settings → the minimal LegacyGamSettings
 * shape that findActiveBadgePack reads (only `activeBadgePackId`).
 * The backend uses `selectedBadgePackId`; legacy transformer expects
 * `activeBadgePackId`. The portal route returns only that field (an
 * employee allow-list, audit G24 part), not the admin settings row.
 */
function toFindPackSettings(
  s: PortalGamificationSettings | null | undefined,
): LegacyGamSettings | null {
  if (!s) return null;
  return { activeBadgePackId: s.selectedBadgePackId ?? null } as LegacyGamSettings;
}

// Map backend PointTransaction shape → legacy award-entry shapes
// used by transform-portal-data transformers.

function ptToPointsAward(pt: PointTransaction): PointsAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    recipientId: pt.recipientId,
    dateTime: pt.createdAt,
    points: pt.points,
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    activity: pt.description || String(pt.eventType),
    moduleType: pt.sourceType,
    campaignId: meta.campaignId ? String(meta.campaignId) : undefined,
    campaignName: meta.campaignName ? String(meta.campaignName) : undefined,
    trainingType: meta.trainingType ? String(meta.trainingType) : undefined,
    trainingName: meta.trainingName ? String(meta.trainingName) : undefined,
  };
}

function ptToBadgeAward(pt: PointTransaction): BadgeAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    recipientId: pt.recipientId,
    dateTime: pt.createdAt,
    badgeName: String(meta.badgeName ?? ''),
    badgeIcon: String(meta.badgeIcon ?? ''),
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    event: String(pt.eventType),
    eventCount: typeof meta.eventCount === 'number' ? meta.eventCount : 1,
  };
}

function ptToLevelAward(pt: PointTransaction): LevelAwardEntry {
  const meta = (pt.metadata ?? {}) as Record<string, unknown>;
  return {
    id: pt.id,
    recipientId: pt.recipientId,
    dateTime: pt.createdAt,
    levelName: String(meta.levelName ?? ''),
    levelIcon: String(meta.levelIcon ?? ''),
    employeeName: String(meta.recipientName ?? ''),
    employeeEmail: String(meta.recipientEmail ?? ''),
    pointsRequired: typeof meta.pointsRequired === 'number' ? meta.pointsRequired : 0,
    previousLevel: String(meta.previousLevel ?? ''),
  };
}

/**
 * Coerce PortalEnrollment (portal-JWT endpoint shape) → EnrollmentLike
 * consumed by the transform-portal-data functions.
 *
 * The portal endpoint pre-scopes enrollments to the recipient, so
 * `learnerEmail` is always the logged-in user's email.
 */
function portalEnrollmentToLike(
  e: PortalEnrollment,
  recipientEmail: string,
) {
  return {
    // FIX #681/#682: pass enrollment id so transformers can surface enrollmentId
    // on TrainingSlideProp / CourseCardProp for player navigation.
    id: e.id,
    learnerEmail: recipientEmail,
    courseId: e.courseId,
    courseName: e.courseName,
    // FIX #670 #671 #672: preserve Title Case status from BE
    // ('Completed' | 'In Progress' | 'Overdue' | 'Not Started' | ...).
    // The previous .toUpperCase().replace(/\s/g, '_') converted them to
    // UPPER_SNAKE_CASE which broke every status comparison in portal-preview.tsx
    // (evalTrained, evalExpired, carousel filter all compare against Title Case).
    // Result: Trained=0, Expired/Overdue=0, Completed courses leaked into slides.
    status: e.status,
    progress: e.progress ?? e.progressPercent ?? 0,
    dueDate: e.dueDate ?? '',
    // The zone the due date is a day in (the enrolment's workspace), so it is
    // shown as that day and not the learner's browser day (TZM-F-01).
    dueTimeZone: e.dueTimeZone,
    completionDate: e.completedAt ?? null,
    // The course's picture: this page has no course list to hand the
    // transforms, so without it every training card showed the default
    // picture (spec 2026-10-07 global deck catalog, §WP5 / Q7).
    courseThumbnailUrl: e.courseThumbnailUrl,
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PortalDashboardInner() {
  const { t } = useTranslation();
  // Identity — set at direct-login time; no round-trip
  const recipient = useMemo(() => portalAuthApi.getRecipient(), []);
  const workspaceId = recipient?.workspaceId ?? '';
  const recipientEmail = recipient?.email ?? '';
  const recipientName = recipient
    ? `${recipient.firstName ?? ''} ${recipient.lastName ?? ''}`.trim() || recipientEmail
    : '';

  // ── Section routing from ?section= query param ───────────────────────────
  // Subroutes (/portal/badges, /portal/training, …) redirect here with a
  // ?section=<name> param. We read it once on mount to: (a) activate the
  // right bottom-strip tab via initialActivePortalTab, and (b) scroll to
  // the anchor element after PortalPreview renders.
  const searchParams = useSearchParams();
  const sectionParam = searchParams?.get('section') ?? '';

  // Used only by the session-recovery effect further down (see 2026-09-21).
  const router = useRouter();
  const pathname = usePathname();
  const search = searchParams?.toString() ?? '';

  // Map section name → PortalPreview tab index (0-4)
  const SECTION_TAB: Record<string, number> = {
    training: 0,
    trainings: 0,
    leaderboard: 1,
    points: 2,
    badges: 3,
    badge: 3,
    certificates: 4,
    certificate: 4,
    // #676 — `dashboard` is a no-op sentinel that lands the user on the
    // default portal view (scrolls to top, no tab override). Without this
    // entry, deep-links like /portal?section=dashboard would be treated
    // as "unknown section" and look like the param was silently ignored.
  };
  // Section names that scroll to an anchor instead of (or in addition to)
  // activating a tab.
  const SECTION_ANCHOR: Record<string, string> = {
    news: 'portal-section-news',
    training: 'portal-section-tabs',
    trainings: 'portal-section-tabs',
    leaderboard: 'portal-section-tabs',
    points: 'portal-section-tabs',
    badges: 'portal-section-tabs',
    badge: 'portal-section-tabs',
    certificates: 'portal-section-tabs',
    certificate: 'portal-section-tabs',
    // #676 — `dashboard` deliberately resolves to the page top (no anchor).
    dashboard: '',
  };

  const initialActivePortalTab = sectionParam ? SECTION_TAB[sectionParam] : undefined;

  useEffect(() => {
    if (!sectionParam) return;
    const anchorId = SECTION_ANCHOR[sectionParam];
    // #676 — `dashboard` sentinel resolves to empty anchor → scroll to top.
    // Unknown sections are silently ignored (debug-logged so we can spot drift).
    if (anchorId === undefined) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(`[portal] unknown ?section=${sectionParam} — known: ${Object.keys(SECTION_ANCHOR).join(', ')}`);
      }
      return;
    }

    // FIX #784 (2026-05-25): the previous `setTimeout(150)` raced against
    // `<PortalPreview>` mount — on slow loads (suspense, async data fetch)
    // the anchor didn't exist in the DOM yet, `getElementById` returned
    // null, and the scroll silently no-op'd. Replaced with a bounded
    // requestAnimationFrame poll (~60 frames @ 16ms = ~1s window) that
    // mirrors the existing `scrollToTrainings` pattern in
    // `app/settings/portal/_components/portal-preview.tsx:402-414`.
    // The empty-anchor 'dashboard' sentinel still scrolls to top
    // immediately — no anchor to wait for.
    if (anchorId === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    let rafId: number | null = null;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~1 second @ 60fps — covers Suspense + first paint

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        rafId = window.requestAnimationFrame(tryScroll);
      } else if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug(`[portal] anchor #${anchorId} not found after ${MAX_ATTEMPTS} frames — giving up`);
      }
    };

    rafId = window.requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam]);

  // UI state
  const [previewDarkMode, setPreviewDarkMode] = useState(false);
  const [isManagerView, setIsManagerView] = useState(false);
  const [managerViewTab, setManagerViewTab] = useState<'dashboard' | 'myteam'>('dashboard');

  // ── Data fetches ──────────────────────────────────────────────────────────

  // Portal-settings: public variant (no admin JWT needed; workspace-level)
  const { data: portalSettingsResolved, isLoading: loadingSettings } =
    usePublicPortalSettings(workspaceId);

  // News: portal-JWT scoped, server-side filters publish-window + targeting
  const { data: newsRawData, isLoading: loadingNews } = usePortalNewsFeed();

  // Gamification settings + badge packs: portal-JWT scoped (workspace-level config)
  const {
    data: gamSettings,
    isLoading: loadingGamSettings,
    error: gamSettingsError,
  } = usePortalGamificationSettings();
  const { data: badgePacks, isLoading: loadingPacks, error: badgePacksError } =
    usePortalBadgePacks();

  // Points activity: portal-JWT scoped, auto-filtered to this recipient
  const {
    data: pointsHistoryPage,
    isLoading: loadingPoints,
    error: pointsHistoryError,
  } = usePortalPointsHistory({ limit: 200 });

  // Earned badges: portal-JWT scoped
  const {
    data: earnedBadgesRaw,
    isLoading: loadingBadges,
    error: earnedBadgesError,
  } = usePortalEarnedBadges();

  // Leaderboard: portal-JWT scoped
  const {
    data: leaderboardData,
    isLoading: loadingLeaderboard,
    error: leaderboardError,
  } = usePortalLeaderboard({
    scope: 'Workspace',
    period: 'All Time',
    limit: 10,
  });

  // Level roadmap: the balance, the level the API picked from it, and the
  // workspace's level ladder — all portal-JWT scoped (audit G9).
  const {
    data: pointsSummary,
    isLoading: loadingPointsSummary,
    error: pointsSummaryError,
  } = usePortalPointsSummary();
  const { data: earnedLevel, isLoading: loadingEarnedLevel, error: earnedLevelError } =
    usePortalLevels();
  const { data: levelLadder, isLoading: loadingLevelLadder, error: levelLadderError } =
    usePortalLevelLadder();

  // Gamification switched off for this tenant: every portal gamification
  // route answers 403 module_disabled (audit 2026-09-25 G17). Hide those
  // sections and fire no gamification celebration — rather than a strip of
  // zeros, "Your Rank #1" (the rank of an empty leaderboard) and empty tabs.
  const gamificationOn = ![
    gamSettingsError,
    badgePacksError,
    pointsHistoryError,
    earnedBadgesError,
    leaderboardError,
    pointsSummaryError,
    earnedLevelError,
    levelLadderError,
  ].some((err) => isModuleDisabledError(err, 'gamification'));

  // Enrollments: portal-JWT scoped, auto-filtered to this recipient
  const { data: enrollmentsRaw, isLoading: loadingEnrollments } = usePortalEnrollments();

  // FIX #686: real issued certificates from course_completions (portal JWT).
  // Previously certificateCards was built from enrollment status (always
  // "In Progress" for incomplete rows) instead of actual issuance events.
  // Now we load from GET /v1/portal/certificates which only returns rows that
  // have a course_completions record (i.e. genuinely issued certificates).
  const { data: issuedCertsRaw } = usePortalCertificates();

  // FIX #668 #673: fetch dashboard summary for real simulation stats + riskStatus.
  // This avoids the synthetic fallback (sent=3, riskLevel='Low') that was
  // hardcoded because portalUser.timesCompromised was never populated.
  const { data: portalDashboard } = usePortalDashboard();

  const loading =
    loadingSettings ||
    loadingNews ||
    loadingGamSettings ||
    loadingPacks ||
    loadingPoints ||
    loadingBadges ||
    loadingLeaderboard ||
    loadingPointsSummary ||
    loadingEarnedLevel ||
    loadingLevelLadder ||
    loadingEnrollments;

  // ── Derived data (memoised) ───────────────────────────────────────────────

  const portalSettings = portalSettingsResolved?.settings ?? null;

  // Policies in Important Updates come from the knowledge library (knowledge
  // wave K1): the five most recently updated documents the learner may see,
  // read only while the section's switch is on. Before K1 nothing fed them.
  const policyItems = useLatestPolicyItems(portalSettings?.showPolicies !== false);

  // The Ask panel lives in the layout; it offers a manager the manager
  // documents while this page's manager view is on.
  const setAskManagerView = useKnowledgeAskStore((s) => s.setManagerView);
  useEffect(() => {
    setAskManagerView(isManagerView);
    return () => setAskManagerView(false);
  }, [isManagerView, setAskManagerView]);

  // Sync dark-mode default from settings on first resolved value.
  useMemo(() => {
    if (portalSettings?.defaultEndUserTheme === 'Dark') {
      setPreviewDarkMode(true);
    }
    // Intentional single-dep memo — only apply settings-driven default once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalSettings?.defaultEndUserTheme]);

  // Points: ActivityLogPage rows → PointsAwardEntry[]
  const pointsAwards = useMemo<PointsAwardEntry[]>(
    () => (pointsHistoryPage?.rows ?? []).map(ptToPointsAward),
    [pointsHistoryPage],
  );

  // What the celebration detector may announce: LIVE awards only — a revoked
  // award row and a revoke's negative reversal row are not something the
  // learner just earned (audit wave 3 item 10; celebratableAwards).
  const celebratedPointsAwards = useMemo<PointsAwardEntry[]>(
    () => celebratableAwards(pointsHistoryPage?.rows ?? []).map(ptToPointsAward),
    [pointsHistoryPage],
  );

  // Badge awards (for the celebrations): the live award rows of badges
  const badgeAwards = useMemo<BadgeAwardEntry[]>(
    () =>
      celebratableAwards(pointsHistoryPage?.rows ?? [])
        .filter((pt) => pt.sourceType === 'BADGE')
        .map(ptToBadgeAward),
    [pointsHistoryPage],
  );

  // Level awards (for the celebrations): the level-ups an admin has not
  // revoked — 0-point 'Level Up' rows, which celebratableAwards (points > 0)
  // would drop (item 1)
  const levelAwards = useMemo<LevelAwardEntry[]>(
    () =>
      celebratableLevelUps(
        (pointsHistoryPage?.rows ?? []).filter((pt) => pt.sourceType === 'LEVEL'),
      ).map(ptToLevelAward),
    [pointsHistoryPage],
  );

  // Active badge pack + derived transformations
  const activeBadgePack = useMemo(
    () => findActiveBadgePack(badgePacks ?? [], toFindPackSettings(gamSettings)),
    [badgePacks, gamSettings],
  );

  // Earned badge ids for transformToBadgeCards, with the global badge each is
  // a copy of: after an admin customises a built-in pack the portal shows the
  // tenant's copy, and a badge earned before that is held under the original.
  const earnedBadgeIds = useMemo<string[]>(
    () => earnedBadgeKeys(earnedBadgesRaw ?? []),
    [earnedBadgesRaw],
  );

  // The awards of badges this learner still holds — the badge cards' earned
  // dates. Not filtered by e-mail: these endpoints return the caller's own rows
  // and no award row carries an e-mail, so that filter dropped every award.
  const heldBadgeAwards = useMemo<BadgeAwardEntry[]>(
    () =>
      awardsForHeldBadges(pointsHistoryPage?.rows ?? [], earnedBadgesRaw ?? []).map(
        ptToBadgeAward,
      ),
    [pointsHistoryPage, earnedBadgesRaw],
  );

  // Leaderboard entries
  const leaderboardEntries = leaderboardData?.entries ?? [];
  const thisEntry = leaderboardEntries.find((e) => e.id === recipient?.id);
  // The learner's rank and total come from the API, ranked in SQL across the
  // whole workspace (leaderboard `currentUser`, item 5 — audit G19). The page
  // worked them out itself: its top-10 row or `entries.length + 1` (everyone
  // below 10th read "#11"), and a sum of the history rows it had loaded. An
  // unranked viewer comes back as rank 0.
  const ownRank =
    leaderboardData?.currentUser && leaderboardData.currentUser.rank > 0
      ? leaderboardData.currentUser
      : null;
  const totalPoints = ownRank?.totalPoints ?? pointsSummary?.totalPoints ?? 0;
  const employeeRank = ownRank?.rank ?? 0;
  const badgeCount = thisEntry?.badgeCount ?? earnedBadgesRaw?.length ?? 0;
  const employeeBadges = thisEntry?.badges ?? [];

  // The roadmap: the workspace ladder, the learner's rung on it (the level
  // GET /v1/portal/levels/earned picked), their balance and their badges.
  // Without it PortalPreview showed "Level 0" and an empty roadmap (audit G9).
  const { levelData, userProgress } = useMemo(
    () =>
      buildLevelProgress({
        levels: levelLadder ?? [],
        earnedLevel: earnedLevel ?? null,
        totalPoints: pointsSummary?.totalPoints ?? totalPoints,
        achievedBadgeIds: achievedBadgeIdsIn(activeBadgePack, earnedBadgesRaw ?? []),
      }),
    [levelLadder, earnedLevel, pointsSummary, totalPoints, activeBadgePack, earnedBadgesRaw],
  );

  // FIX #673: map the DB riskStatus enum (None/Safe/Low/Medium/High/Very High)
  // to the portal risk level ('Low'|'Medium'|'High') used by portal-preview
  // to render the Risk Score widget. Previously hardcoded to 'Low' → always 25.
  const derivedRiskLevel = useMemo<'Low' | 'Medium' | 'High'>(() => {
    const rs = portalDashboard?.riskStatus ?? '';
    // 'Critical' is the one risk engine's top level (DHA W2-01); absent here
    // it fell to 'Low', the exact inversion a learner would read.
    if (rs === 'High' || rs === 'Very High' || rs === 'Critical') return 'High';
    if (rs === 'Medium') return 'Medium';
    return 'Low';
  }, [portalDashboard?.riskStatus]);

  // PortalUser shape — isManager is sourced from the portal-JWT claim that
  // the BE embeds at issuance (recipients.manager_id back-ref check).
  // Legacy tokens issued before that claim existed default to false; the
  // toggle stays hidden until the user re-logs.
  const portalUser = useMemo<PortalUser | null>(() => {
    if (!recipient) return null;
    // FIX #668: use real compromised count from simulation stats.
    const realCompromised = portalDashboard?.simulationStats?.totalCompromised ?? 0;
    // FIX #673: use real training completion rate from dashboard stats.
    const realTrainingCompletion = portalDashboard?.trainingStats?.completionRate ?? 0;
    return {
      id: recipient.id,
      name: recipientName,
      email: recipientEmail,
      upn: recipientEmail,
      department: '',
      designation: '',
      location: '',
      avatar: '',
      hasPortalAccess: true,
      totalPoints,
      badgeCount,
      badges: employeeBadges,
      rank: employeeRank,
      riskLevel: derivedRiskLevel,
      timesCompromised: realCompromised,
      trainingCompletion: realTrainingCompletion,
      reportRate: 0,
      isManager: recipient.isManager === true,
      directReports: 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipient, totalPoints, badgeCount, employeeRank, derivedRiskLevel, portalDashboard]);

  // Leaderboard prop (top 5) for PortalPreview
  const leaderboardProp = useMemo(
    () =>
      leaderboardEntries.slice(0, 5).map((e) => ({
        rank: e.rank,
        name: e.name,
        dept: e.department,
        points: e.totalPoints,
        isCurrentUser: e.id === recipient?.id,
      })),
    [leaderboardEntries, recipient?.id],
  );

  // News: usePortalNewsFeed returns PortalNews[] directly.
  // transformNewsToProps expects NewsUpdate[] (legacy NC shape). The fields
  // overlap: PortalNews has title, content (→ description), thumbnailUrl,
  // publishStart, publishEnd, isActive. Coerce inline.
  const newsItems = useMemo(() => {
    const rows = Array.isArray(newsRawData) ? newsRawData : [];
    if (rows.length === 0) return undefined;
    // Portal feed is already server-filtered to published+window+targeting,
    // so pass isActive=true for all rows to skip client-side filter in transformer.
    const asNewsUpdate = rows.map((n) => ({
      isActive: true,
      title: n.title ?? '',
      content: n.content ?? '',
      thumbnailUrl: n.thumbnailUrl ?? '',
      publishStart: n.publishStart ?? n.createdAt ?? new Date().toISOString(),
      publishEnd: n.publishEnd ?? new Date(Date.now() + 86400000 * 365).toISOString(),
      createdOn: n.createdAt ?? '',
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return transformNewsToProps(asNewsUpdate as any);
  }, [newsRawData]);

  // Enrollments: coerce PortalEnrollment → EnrollmentLike for transformers
  const enrollmentLikes = useMemo(
    () => (enrollmentsRaw ?? []).map((e) => portalEnrollmentToLike(e, recipientEmail)),
    [enrollmentsRaw, recipientEmail],
  );

  // The workspace zone the API names on each enrolment. The transforms print a
  // due date as that zone's day — each row in its own zone, this one for any
  // row without it — never the learner's browser day (TZM-F-01).
  const dueTimeZone = useMemo(
    () => enrollmentsRaw?.find((e) => e.dueTimeZone)?.dueTimeZone,
    [enrollmentsRaw],
  );

  // Training slides (in-progress courses)
  const trainingSlides = useMemo(
    () =>
      enrollmentLikes.length > 0
        ? transformToTrainingSlides(enrollmentLikes, [], recipientEmail, dueTimeZone)
        : undefined,
    [enrollmentLikes, recipientEmail, dueTimeZone],
  );

  // Course cards
  const courseCards = useMemo(
    () =>
      enrollmentLikes.length > 0
        ? transformToCourseCards(enrollmentLikes, [], recipientEmail, dueTimeZone)
        : undefined,
    [enrollmentLikes, recipientEmail, dueTimeZone],
  );

  // FIX #686: build certificateCards from ACTUAL issued certs
  // (GET /v1/portal/certificates → course_completions rows) instead of
  // deriving status from enrollment.status which was always "In Progress"
  // for any course that hadn't yet generated a completion record.
  // Each PortalCertificate row only exists when a cert was genuinely issued;
  // its certificateIssuedAt is always set, so status is always "Completed".
  const certificateCards = useMemo(() => {
    const certs = issuedCertsRaw ?? [];
    if (certs.length === 0) return undefined;
    return certs.map((c) => ({
      // Raw values: <PortalPreview> titles the card "{course} Certificate" and
      // dates it ("Sep 30, 2026" / "30 سبتمبر 2026") in the reader's language.
      name: c.courseName,
      date: c.certificateIssuedAt || c.completionDate || null,
      // All rows from course_completions are definitively issued — "Completed".
      // An enum value, not display copy: the portal translates it when shown.
      status: 'Completed' as const,
    }));
  }, [issuedCertsRaw]);

  // Points history (this recipient only — matched by id: the portal's rows
  // carry no name, so matching by name emptied the tab, lead item E). Each
  // award keeps its instant; <PortalPreview> words it ("5 min ago" / "قبل 5
  // دقائق") in the reader's language (locale audit 2026-09-26, L-13).
  const pointsHistory = useMemo(
    () =>
      pointsAwards.length > 0
        ? transformPointsHistory(pointsAwards, recipient?.id)
        : undefined,
    [pointsAwards, recipient?.id],
  );

  // Badge cards (requires activeBadgePack from portal-JWT usePortalBadgePacks)
  const badgeCards = useMemo(
    () =>
      activeBadgePack
        ? transformToBadgeCards(
            activeBadgePack,
            earnedBadgeIds,
            heldBadgeAwards,
            pointsAwards,
          )
        : undefined,
    [activeBadgePack, earnedBadgeIds, heldBadgeAwards, pointsAwards],
  );

  // ── Session recovery (production report, 2026-09-21) ─────────────────────
  //
  // `portalUser` is built from `hf-portal:recipient`, so it is null whenever
  // that key is gone — a token that is live but whose recipient was never
  // stored, or was cleared on its own. The gate above cannot see that: it reads
  // the TOKEN. This branch used to render terminal text ("Unable to load your
  // portal. Please log in again.") with nothing to click.
  //
  // Recover the same way the gate does: clear whatever is left of the session
  // and go to the portal login, carrying the deep link through. `clearSession()`
  // is what stops this becoming a loop — a half-session left in storage would
  // send the visitor straight back here.
  const sessionLost = !loading && !portalUser;
  const recoveringRef = useRef(false);
  useEffect(() => {
    if (!sessionLost || recoveringRef.current) return;
    recoveringRef.current = true;
    portalAuthApi.clearSession();
    router.replace(buildPortalLoginHref(pathname, search));
  }, [sessionLost, router, pathname, search]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return <PortalDashboardSkeleton />;
  }

  if (!portalUser) {
    // Neutral spinner while the replace above lands — never the dead end.
    return (
      <div
        className="flex items-center justify-center py-24"
        role="status"
        aria-live="polite"
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60" />
        <span className="sr-only">{t('portal.shell.takingYouToSignIn')}</span>
      </div>
    );
  }

  return (
    // The (portal) layout already sets its <main> to full-width on the
    // dashboard route + suppresses its header so <PortalPreview> can own
    // the chrome. We just render fullscreen here. inline={true} keeps
    // PortalPreview from rendering its admin-style back-button strip.
    <div className="w-full">
      <CelebrationProvider employeeId={portalUser.id}>
        <CelebrationRunner
          user={portalUser}
          pointsAwards={celebratedPointsAwards}
          badgeAwards={badgeAwards}
          levelAwards={levelAwards}
          gamificationEnabled={gamificationOn}
        />
        <PortalPreview
          inline
          selectedTheme={portalSettings?.selectedTheme ?? 'modern-teal'}
          previewDarkMode={previewDarkMode}
          onPreviewDarkModeToggle={() => {
            // FIX #684: On the real employee portal (inline=true) the toggle must
            // update <html>.class so next-themes CSS vars take effect globally.
            // The preview component only flips its own container's "dark" class —
            // that was fine in the admin preview iframe but not for the full-page
            // portal. We do it here (in the live-portal callsite) rather than in
            // the shared PortalPreview component so we don't break the admin
            // preview which intentionally scopes dark-mode to its container only.
            setPreviewDarkMode((v) => {
              const next = !v;
              if (typeof document !== 'undefined') {
                document.documentElement.classList.toggle('dark', next);
              }
              return next;
            });
          }}
          onClose={() => undefined}
          portalLogoLight={portalSettings?.portalLogoLight ?? null}
          portalLogoDark={portalSettings?.portalLogoDark ?? null}
          // Unset falls back to the stock copy, which PortalPreview shows in the
          // reader's language (so does a stored value that is still the seed).
          heroHeading={portalSettings?.heroHeading ?? t('portal.copy.heroHeading')}
          heroDescription={portalSettings?.heroDescription ?? t('portal.copy.heroDescription')}
          heroImage={portalSettings?.heroImage ?? null}
          ctaButtonText={portalSettings?.ctaButtonText ?? t('portal.copy.cta')}
          coverImage={portalSettings?.coverImage ?? null}
          showStatsBar={gamificationOn && portalSettings?.showStatsBar !== false}
          showImportantUpdates={portalSettings?.showImportantUpdates !== false}
          showAssignedTraining={portalSettings?.showAssignedTraining !== false}
          showNewsUpdates={portalSettings?.showNewsUpdates !== false}
          showPolicies={portalSettings?.showPolicies !== false}
          showProgressSection={portalSettings?.showProgressSection !== false}
          showYourRank={gamificationOn && portalSettings?.showYourRank !== false}
          showPoints={gamificationOn && portalSettings?.showPoints !== false}
          showBadges={gamificationOn && portalSettings?.showBadges !== false}
          showCertificates={portalSettings?.showCertificates !== false}
          showRiskScore={portalSettings?.showRiskScore !== false}
          showCompromised={portalSettings?.showCompromised !== false}
          showAcknowledged={portalSettings?.showAcknowledged !== false}
          showEvaluated={portalSettings?.showEvaluated !== false}
          showTrainings={portalSettings?.showTrainings !== false}
          showLeaderboard={gamificationOn && portalSettings?.showLeaderboard !== false}
          showPointsTab={gamificationOn && portalSettings?.showPointsTab !== false}
          showBadgesTab={gamificationOn && portalSettings?.showBadgesTab !== false}
          showCertificatesTab={portalSettings?.showCertificatesTab !== false}
          isManagerView={isManagerView}
          onManagerViewChange={setIsManagerView}
          managerViewTab={managerViewTab}
          onManagerViewTabChange={setManagerViewTab}
          // Employee-specific identity
          employeeName={portalUser.name}
          employeeAvatar={portalUser.avatar}
          employeeEmail={portalUser.email}
          employeeDepartment={portalUser.department}
          employeeRank={portalUser.rank}
          employeeTotalPoints={portalUser.totalPoints}
          employeeBadgeCount={portalUser.badgeCount}
          employeeBadges={portalUser.badges}
          employeeRiskLevel={portalUser.riskLevel}
          employeeTimesCompromised={portalUser.timesCompromised}
          employeeTrainingCompletion={portalUser.trainingCompletion}
          employeeReportRate={portalUser.reportRate}
          isManager={portalUser.isManager}
          leaderboardData={leaderboardProp}
          // FIX #668: pass real phishing simulation stats so portal-preview renders
          // actual Sent/Clicked/Compromised counts instead of the synthetic fallback
          // (sent = Math.max(timesCompromised*3, 3) = 3 when timesCompromised=0).
          phishingStats={
            portalDashboard?.simulationStats
              ? {
                  sent: portalDashboard.simulationStats.totalReceived,
                  opened: 0, // not tracked at recipient level — omit for now
                  clicked: portalDashboard.simulationStats.totalClicked,
                  compromised: portalDashboard.simulationStats.totalCompromised,
                  reported: portalDashboard.simulationStats.totalReported,
                  trained: portalDashboard.trainingStats?.totalCompleted ?? 0,
                  evaluated: portalDashboard.trainingStats?.totalCompleted ?? 0,
                  campaignCount: portalDashboard.simulationStats.totalReceived,
                  lastIncident: null,
                }
              : null
          }
          // Real data from portal-JWT hooks
          newsItems={newsItems}
          policyItems={policyItems}
          trainingSlides={trainingSlides}
          courseCards={courseCards}
          pointsHistory={pointsHistory}
          badgeCards={badgeCards}
          certificateCards={certificateCards}
          activeBadgePack={activeBadgePack}
          userProgress={userProgress}
          levelData={levelData}
          // The learner's open challenges (item 3); none while Gamification is off.
          // Their days are the workspace's, the zone the enrolments name.
          challengesSection={gamificationOn ? <PortalChallengesSection timeZone={dueTimeZone} /> : null}
          // The policy assistant's "Ask AI" button in the home header, as the main
          // app has it (it shows only while an assistant is available).
          headerActions={<KnowledgeAskAiButton />}
          initialActivePortalTab={initialActivePortalTab}
        />
      </CelebrationProvider>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Celebration detector (must live inside CelebrationProvider)
// ---------------------------------------------------------------------------

function CelebrationRunner({
  user,
  pointsAwards,
  badgeAwards,
  levelAwards,
  gamificationEnabled,
}: {
  user: PortalUser;
  pointsAwards: PointsAwardEntry[];
  badgeAwards: BadgeAwardEntry[];
  levelAwards: LevelAwardEntry[];
  gamificationEnabled: boolean;
}) {
  useCelebrationDetector({ user, pointsAwards, badgeAwards, levelAwards, gamificationEnabled });
  return null;
}

// ---------------------------------------------------------------------------
// Session gate (admin bounce: audit P1-1, 2026-05-06;
//               login redirect: production report, 2026-09-21)
//
// The /portal route is the EMPLOYEE-facing dashboard — every data hook in
// PortalDashboardInner uses fetchPortalApi which carries the portal JWT
// (`hf-portal:token`). When an admin lands here directly (typed URL or stale
// bookmark) they have an admin JWT but no portal JWT, so all 14 GETs to
// /v1/portal/* (dashboard, news, leaderboard, badges, gamification-settings,
// points/history, badges/earned, enrollments, etc.) 401 and the page renders
// "Unable to load your portal. Please log in again."
//
// The admin's actual intent is the in-app portal preview, which already
// exists at /settings/portal?tab=view-as (renders <PortalPreview> with
// admin-side hooks — no portal JWT required). Bounce admins there BEFORE
// PortalDashboardInner mounts so its hooks never fire. Employees with a real
// portal token continue to the dashboard untouched.
//
// 2026-09-21 — REPORTED FROM PRODUCTION. The gate above handled exactly that
// ONE case and let every other case fall through to `setDecision('allow')`.
// With NO admin token and NO portal token — never logged in, logged out, a
// shared or typed URL — the dashboard mounted, every GET 401'd, and the page
// rendered one line of grey text: "Unable to load your portal. Please log in
// again." No link, no button, no redirect. The message told the person to log
// in and gave them no way to do it.
//
// So the gate now answers the question it was always shaped to answer: is there
// a LIVE portal session here? `portalAuthApi.isAuthenticated()` is the existing
// answer — false for a missing token AND for a present-but-expired one (it
// decodes `exp`; added 2026-05-22 for exactly this class of bug on the player).
// Reading it instead of `getToken()` folds both dead-end states into one branch
// and keeps the whole point of the gate intact: nothing mounts, so the 401 storm
// never happens.
//
// The admin branch is checked FIRST and widened the same way — an admin holding
// a stale, expired portal token still wants the view-as preview, not an employee
// login form.
// ---------------------------------------------------------------------------
function AdminRedirectGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // `searchParams` is a fresh object on some renders; the STRING is the value
  // this effect actually depends on.
  const search = searchParams?.toString() ?? '';
  // Tri-state: 'unknown' (not yet checked, SSR/first paint), 'allow' (employee),
  // 'bouncing' (admin → settings/portal?tab=view-as, or no session → portal login).
  const [decision, setDecision] = useState<'unknown' | 'allow' | 'bouncing'>(
    'unknown',
  );
  // One bounce per mount. Without this a re-render that changed `search` could
  // fire a second router.replace while the first is still in flight.
  const bouncedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (bouncedRef.current) return;
    let cancelled = false;
    const checkSession = async () => {
      const adminToken = window.localStorage.getItem('hf-app:access-token');
      let hasLivePortalSession = portalAuthApi.isAuthenticated() &&
        !isPortalTokenDeadLocally(portalAuthApi.getToken());
      if (!hasLivePortalSession && portalAuthApi.getRefreshToken()) {
        await portalAuthApi.refresh();
        // Another tab or login may have replaced this session while renewal
        // waited. Decide from current storage, not the older flight's result.
        hasLivePortalSession = portalAuthApi.isAuthenticated() &&
          !isPortalTokenDeadLocally(portalAuthApi.getToken());
      }
      if (cancelled) return;

      if (adminToken && !hasLivePortalSession) {
        bouncedRef.current = true;
        setDecision('bouncing');
        router.replace('/settings/portal?tab=view-as');
        return;
      }

      if (!hasLivePortalSession) {
        // Drop the dead token (and the recipient/refresh alongside it) so the
        // login page starts from a clean session. Leaving it there is what would
        // turn this redirect into a loop the moment any other surface decided a
        // present token means "signed in".
        if (portalAuthApi.getToken()) portalAuthApi.clearSession();
        // PURGE A WORKSPACE ID THE SHELL CANNOT RESOLVE.
        //
        // Stripping the junk `ws` out of the redirect URL is not enough: the
        // (portal) layout falls back to sessionStorage the moment the query
        // param is gone, so the login page we are sending this visitor to would
        // read the same poisoned value back. It then gates its children on
        // `!!workspaceId && settingsLoading`, /portal-settings/public 400s on an
        // id that is not a uuid, and the sign-in form never paints — the visitor
        // is handed a spinner instead of the thing they were redirected to reach.
        // Verified in a browser on 2026-09-21.
        try {
          const storedWs = window.sessionStorage.getItem(PORTAL_WS_STORAGE_KEY);
          if (storedWs !== null && !isResolvableWorkspaceId(storedWs)) {
            window.sessionStorage.removeItem(PORTAL_WS_STORAGE_KEY);
          }
        } catch {
          // Private mode / blocked storage — nothing to purge, and a redirect
          // must never be prevented by storage being unavailable.
        }
        bouncedRef.current = true;
        setDecision('bouncing');
        router.replace(buildPortalLoginHref(pathname, search));
        return;
      }

      setDecision('allow');
    };
    void checkSession();
    return () => { cancelled = true; };
  }, [router, pathname, search]);

  if (decision !== 'allow') {
    // 'unknown' (briefly during hydration) and 'bouncing' (replacing the route)
    // both render the same neutral spinner — never the dead-end text. Crucially
    // we DO NOT render PortalDashboardInner here, which would mount the 14
    // portal-JWT hooks and 401-storm before the redirect lands.
    return (
      <div
        className="flex items-center justify-center py-24"
        role="status"
        aria-live="polite"
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60" />
        <span className="sr-only">{t('portal.shell.loading')}</span>
      </div>
    );
  }
  return <>{children}</>;
}

/** The Suspense fallback: a spinner that names what it waits for. */
function PortalLoadingStatus() {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center justify-center py-24"
      role="status"
      aria-live="polite"
    >
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60" />
      <span className="sr-only">{t('portal.shell.loading')}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suspense wrapper — required by Next.js because PortalDashboardInner uses
// useSearchParams() during SSG. Without this, the build fails on prerender.
// ---------------------------------------------------------------------------
export default function PortalDashboard() {
  return (
    <Suspense fallback={<PortalLoadingStatus />}>
      <AdminRedirectGate>
        <PortalDashboardInner />
      </AdminRedirectGate>
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Portal dashboard skeleton — matches the page's section structure so the
// transition to real content is calm (no layout shift). Used during the
// initial multi-query fetch (settings + news + gamification + enrollments).
// ---------------------------------------------------------------------------
function PortalDashboardSkeleton() {
  return (
    <div className="w-full">
      {/* Portal chrome row (logo + toggle + user) */}
      <div className="sticky top-0 z-40 h-16 border-b border-border bg-card flex items-center justify-between px-8">
        <Skeleton className="h-8 w-40" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="space-y-1 hidden md:block">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
      </div>
      <div className="w-full flex justify-center bg-background">
        <div className="w-full max-w-6xl px-8 py-10 space-y-10">
          {/* Hero card */}
          <div className="rounded-2xl border bg-muted/30 p-10 space-y-4">
            <Skeleton className="h-8 w-2/3 max-w-md" />
            <Skeleton className="h-4 w-1/2 max-w-sm" />
            <Skeleton className="h-10 w-40 rounded-md mt-4" />
          </div>
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border bg-card p-5 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
          {/* Tabs row */}
          <div className="flex gap-2">
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
          </div>
          {/* Training grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden">
                <Skeleton className="h-40 w-full" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
